/* ═══════════════════════════════════════════
   FORLASERCUT™ — Landing Page JavaScript
   Countdown · Accordion · Scroll Reveal · Nav
═══════════════════════════════════════════ */

'use strict';

/* ── 1. COUNTDOWN TIMER ────────────────────────────────
   Sets a 4-hour countdown from page load, persisted in
   sessionStorage so refreshing doesn't reset it.
────────────────────────────────────────────────────────*/
(function initCountdowns() {
  const DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours
  const KEY = 'flc_deadline';

  let deadline = parseInt(sessionStorage.getItem(KEY), 10);
  if (!deadline || deadline < Date.now()) {
    deadline = Date.now() + DURATION_MS;
    sessionStorage.setItem(KEY, deadline);
  }

  function formatTime(ms) {
    if (ms <= 0) return '00:00:00';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
  }

  const targets = [
    document.getElementById('countdown'),
    document.getElementById('hero-countdown'),
    document.getElementById('offer-timer'),
    document.getElementById('final-timer'),
  ].filter(Boolean);

  function tick() {
    const remaining = deadline - Date.now();
    const display = formatTime(remaining);
    targets.forEach(el => { el.textContent = display; });
    if (remaining > 0) {
      requestAnimationFrame(() => setTimeout(tick, 1000));
    } else {
      targets.forEach(el => { el.textContent = 'EXPIRED'; });
    }
  }
  tick();
})();

/* ── 2. STICKY NAV SCROLL CLASS ────────────────────────*/
(function initNav() {
  const nav = document.getElementById('nav');
  if (!nav) return;
  const onScroll = () => {
    nav.classList.toggle('scrolled', window.scrollY > 50);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* ── 3. SCROLL REVEAL ──────────────────────────────────
   Adds .visible class to [data-reveal] elements as they
   enter the viewport.
────────────────────────────────────────────────────────*/
(function initReveal() {
  const elements = document.querySelectorAll('[data-reveal]');
  if (!elements.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  elements.forEach((el, i) => {
    // Stagger siblings in the same grid parent
    el.style.transitionDelay = `${(i % 4) * 80}ms`;
    observer.observe(el);
  });
})();

/* ── 4. FAQ ACCORDION ──────────────────────────────────
   Progressive-enhancement accordion using aria-expanded
   and hidden attribute — keyboard accessible.
────────────────────────────────────────────────────────*/
(function initFaq() {
  const init = () => {
    const buttons = document.querySelectorAll('.faq-q');

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const answerId = btn.getAttribute('aria-controls');
        const answer = document.getElementById(answerId);

        // Close all others
        buttons.forEach((other) => {
          if (other !== btn) {
            other.setAttribute('aria-expanded', 'false');
            const otherId = other.getAttribute('aria-controls');
            const otherAns = document.getElementById(otherId);
            if (otherAns) otherAns.hidden = true;
          }
        });

        // Toggle current
        btn.setAttribute('aria-expanded', String(!expanded));
        if (answer) answer.hidden = expanded;
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── 5. SMOOTH ANCHOR SCROLL ───────────────────────────
   Intercepts internal anchor clicks and applies smooth
   scroll with offset for the sticky nav height.
────────────────────────────────────────────────────────*/
(function initSmoothScroll() {
  const NAV_HEIGHT = 100; // approx nav + announcement bar px

  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const id = anchor.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
})();

/* ── 6. CTA BUTTON PULSE ANIMATION ────────────────────
   Adds a subtle glow-pulse animation to primary CTAs
   after a short idle delay to draw attention.
────────────────────────────────────────────────────────*/
(function initCtaPulse() {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes ctaGlow {
      0%, 100% { box-shadow: 0 0 32px rgba(249,115,22,.4), 0 4px 16px rgba(0,0,0,.4); }
      50%       { box-shadow: 0 0 56px rgba(249,115,22,.7), 0 8px 24px rgba(0,0,0,.5); }
    }
    .btn--primary.pulsing {
      animation: ctaGlow 2.5s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);

  setTimeout(() => {
    document.querySelectorAll('.btn--primary').forEach((btn) => {
      btn.classList.add('pulsing');
    });
  }, 3000);
})();

/* ── 7. OFFER SECTION ENTRANCE ─────────────────────────
   Highlights the pricing card when it enters viewport.
────────────────────────────────────────────────────────*/
(function initOfferHighlight() {
  const offerCard = document.querySelector('.offer-card');
  if (!offerCard) return;

  const obs = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        offerCard.style.transition = 'box-shadow .6s ease';
        offerCard.style.boxShadow =
          '0 40px 100px rgba(0,0,0,.5), 0 0 0 2px rgba(249,115,22,.4), inset 0 1px 0 rgba(255,255,255,.05)';
        obs.disconnect();
      }
    },
    { threshold: 0.2 }
  );
  obs.observe(offerCard);
})();

/* ── 8. MOBILE NAV: HIDE ALL LINKS EXCEPT CTA ──────────
   Already handled via CSS display:none on .nav-link at
   ≤768px. This JS ensures the CTA link is always visible.
────────────────────────────────────────────────────────*/

/* ── 9. LAZY-LOAD PERFORMANCE HINT ────────────────────*/
if ('connection' in navigator && navigator.connection.saveData) {
  // Data-saver mode: skip non-critical animations
  document.documentElement.style.setProperty('--section-py', '3.5rem');
}

/* ── 10. ACTIVE CTA TRACKING (placeholder) ────────────
   In PageFly / Shopify, replace these with actual
   analytics or PageFly button click events.
────────────────────────────────────────────────────────*/
(function trackClicks() {
  const ctaIds = ['hero-cta-main', 'nav-cta-btn', 'offer-cta-btn', 'final-cta-btn'];
  ctaIds.forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('click', () => {
      // Replace with: analytics.track('CTA Clicked', { id });
      console.info('[FORLASERCUT] CTA clicked:', id);
    });
  });
})();

/* ── 11. BUNDLE GALLERY TABS ───────────────────────────
   Switches visible panel when a category tab is clicked.
────────────────────────────────────────────────────────*/
(function initBundleGallery() {
  const gallery = document.getElementById('bundle-gallery');
  if (!gallery) return;

  const tabs = gallery.querySelectorAll('.bg-tab');
  const panels = gallery.querySelectorAll('.bg-panel');

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;

      // Update tabs
      tabs.forEach((t) => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');

      // Update panels
      panels.forEach((p) => p.classList.remove('active'));
      const activePanel = gallery.querySelector(`[data-panel="${target}"]`);
      if (activePanel) activePanel.classList.add('active');
    });
  });
})();

/* ── 12. BUY NOW — ADD TO CART + REDIRECT TO CHECKOUT ──
   Intercepts all [data-buy-now] links, adds the product
   to cart via the Shopify AJAX Cart API, then sends the
   customer straight to /checkout.
────────────────────────────────────────────────────────*/
(function initBuyNow() {
  const productData = document.getElementById('flc-product-data');
  const variantId = productData ? parseInt(productData.dataset.variantId, 10) : null;

  if (!variantId) {
    // No variant found — buttons will fall back to normal href behaviour
    console.warn('[FORLASERCUT] Variant ID not found. Buttons will link to product page.');
    return;
  }

  document.querySelectorAll('[data-buy-now]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();

      // Show loading state
      const originalText = btn.innerHTML;
      btn.innerHTML = 'Adding to cart…';
      btn.style.opacity = '0.75';
      btn.style.pointerEvents = 'none';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!res.ok) throw new Error('Cart add failed');

        // Success — go straight to checkout
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[FORLASERCUT] Buy now error:', err);
        // Restore button and fall back to product page
        btn.innerHTML = originalText;
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
        window.location.href = btn.getAttribute('href');
      }
    });
  });
})();
